/**
 * File: contractRecord.ts
 * Path: packages/config/src/contracts/contractRecord.ts
 * Description: Reading a stored contract safely, the empty content each
 * template starts from, and the checks a draft must pass before it is sent.
 */

import { INNOVATIONS, PACKAGES } from "../pricing/packagesData";
import {
  INNOVATION_PRICING,
  PACKAGE_TERM_MONTHS,
  PAYMENT_PLAN_ORDER,
} from "../pricing/paymentConstants";
import type {
  PackageId,
  PaymentPlanId,
  ServiceTermsOverride,
} from "../pricing/pricingTypes";
import { legacyContractReference } from "../references/references";
import { DEFAULT_MONTHLY_DUE_DAY, toMonthlyDueDay } from "./contractDueDay";
import { isIsoDate } from "./contractFormat";
import type {
  Contract,
  ContractAmendment,
  ContractBankDetails,
  ContractClientParty,
  ContractContent,
  ContractDocument,
  ContractImport,
  ContractImportSource,
  ContractSettings,
  ContractSignatureEvidence,
  ContractStage,
  ContractStatus,
  ContractTemplateId,
  ScopeOutline,
  ScopePoint,
  StoredContractFile,
} from "./contractTypes";
import { DEFAULT_CONTRACT_SIGNATORY } from "./contractConstants";

export const CONTRACT_STATUS_LABELS: Record<ContractStatus, string> = {
  draft: "Draft",
  sent: "Sent for signing",
  client_signed: "Signed by client",
  signed: "Fully signed",
};

export const CONTRACT_TEMPLATE_IDS: readonly ContractTemplateId[] = [
  "service",
  "retainer",
  "sprint",
  "venture",
];
const STATUSES: readonly ContractStatus[] = [
  "draft",
  "sent",
  "client_signed",
  "signed",
];

/** Same bound as the clients list: one cheap read, nobody hidden. */
export const CONTRACTS_LIST_LIMIT = 500;

/** Longest scope list and text the form and the rules accept. */
export const CONTRACT_LIMITS = {
  scopeItems: 60,
  scopeItemLength: 400,
  scopeNotesLength: 4000,
  stages: 12,
  partyFieldLength: 300,
  notesLength: 4000,
} as const;

// ─── Defaults ────────────────────────────────────────────────────────────

/** FORGE stage length, from the published pricing. */
export const DEFAULT_SPRINT_WEEKS = INNOVATION_PRICING.forge.sprint_weeks;

/** The low end of VENTURE's published equity range ("10-20%"). */
export const DEFAULT_VENTURE_EQUITY =
  Number.parseInt(INNOVATIONS.venture.equityRange, 10) || 10;

export const emptyClientParty = (): ContractClientParty => ({
  name: "",
  registrationNo: "",
  address: "",
  email: "",
  phone: "",
  signatoryName: "",
  signatoryTitle: "",
});

/** A fresh contract of the given type, priced from the config. */
export const emptyContractContent = (
  template: ContractTemplateId,
  today: string,
): ContractContent => ({
  template,
  client: emptyClientParty(),
  scopeItems: [],
  delivering: [],
  scopeNotes: "",
  commencementDate: today,
  fee: 0,
  paymentPlan: "once_off",
  stages: [],
  packageId: null,
  monthlyFee:
    template === "venture" ? INNOVATION_PRICING.venture.retainer_investment : 0,
  minimumTermMonths:
    template === "venture"
      ? INNOVATION_PRICING.venture.partnership_months
      : PACKAGE_TERM_MONTHS,
  retainerBilling: "monthly",
  monthlyDueDay: DEFAULT_MONTHLY_DUE_DAY,
  blueprintFee: INNOVATION_PRICING.forge.blueprint_investment,
  blueprintCredit: true,
  sprintFee: INNOVATION_PRICING.forge.sprint_investment,
  sprintWeeks: DEFAULT_SPRINT_WEEKS,
  estimatedSprints: 4,
  equityPercent: DEFAULT_VENTURE_EQUITY,
  amendment: null,
});

// ─── Reading stored data ─────────────────────────────────────────────────

type Raw = Record<string, unknown>;

const isObject = (v: unknown): v is Raw =>
  typeof v === "object" && v !== null && !Array.isArray(v);
const text = (v: unknown, max = 10_000): string =>
  typeof v === "string" ? v.slice(0, max) : "";
const num = (v: unknown, fallback = 0): number =>
  typeof v === "number" && Number.isFinite(v) ? v : fallback;
const millis = (v: unknown): number | null => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (
    isObject(v) &&
    typeof (v as { toMillis?: unknown }).toMillis === "function"
  ) {
    return (v as { toMillis: () => number }).toMillis();
  }
  return null;
};

const isTemplate = (v: unknown): v is ContractTemplateId =>
  typeof v === "string" &&
  (CONTRACT_TEMPLATE_IDS as readonly string[]).includes(v);
const isPlan = (v: unknown): v is PaymentPlanId =>
  typeof v === "string" && (PAYMENT_PLAN_ORDER as string[]).includes(v);
const isPackage = (v: unknown): v is PackageId =>
  typeof v === "string" && Object.prototype.hasOwnProperty.call(PACKAGES, v);

const toParty = (v: unknown): ContractClientParty => {
  const r = isObject(v) ? v : {};
  return {
    name: text(r.name, 300),
    registrationNo: text(r.registrationNo, 300),
    address: text(r.address, 300),
    email: text(r.email, 300),
    phone: text(r.phone, 300),
    signatoryName: text(r.signatoryName, 300),
    signatoryTitle: text(r.signatoryTitle, 300),
  };
};

const toStages = (v: unknown): ContractStage[] =>
  Array.isArray(v)
    ? v
        .filter(isObject)
        .slice(0, CONTRACT_LIMITS.stages)
        .map((s) => ({
          label: text(s.label, 200),
          amount: Math.max(0, num(s.amount)),
        }))
    : [];

const toAmendment = (v: unknown): ContractAmendment | null => {
  if (
    !isObject(v) ||
    typeof v.parentContractId !== "string" ||
    !v.parentContractId
  )
    return null;
  return {
    parentContractId: v.parentContractId,
    parentReference: text(v.parentReference, 60),
    parentSignedDate: isIsoDate(v.parentSignedDate) ? v.parentSignedDate : "",
    number: Math.max(1, Math.round(num(v.number, 1))),
    summary: text(v.summary, CONTRACT_LIMITS.scopeNotesLength),
  };
};

const isScopeLine = (v: unknown): v is string =>
  typeof v === "string" && v.length <= CONTRACT_LIMITS.scopeItemLength;
const isScopeList = <T>(v: unknown, is: (x: unknown) => x is T): v is T[] =>
  Array.isArray(v) && v.length <= CONTRACT_LIMITS.scopeItems && v.every(is);
const isScopePoint = (v: unknown): v is ScopePoint =>
  isObject(v) && (v.lead === null || isScopeLine(v.lead)) && isScopeLine(v.text);
const isScopeSection = (v: unknown): v is ScopeOutline["sections"][number] =>
  isObject(v) &&
  isScopeLine(v.number) &&
  isScopeLine(v.title) &&
  isScopeList(v.points, isScopePoint);

/** The stored scope layout, kept only when every part has the right shape. */
const toScopeOutline = (v: unknown): ScopeOutline | undefined => {
  if (
    !isObject(v) ||
    !isScopeList(v.intro, isScopeLine) ||
    !isScopeList(v.sections, isScopeSection)
  )
    return undefined;
  const notIncluded =
    v.notIncluded === null ? null : isScopeLine(v.notIncluded) ? v.notIncluded : undefined;
  if (notIncluded === undefined) return undefined;
  return {
    intro: [...v.intro],
    sections: v.sections.map((s) => ({
      number: s.number,
      title: s.title,
      points: s.points.map((p) => ({ lead: p.lead, text: p.text })),
    })),
    notIncluded,
  };
};

/** The deal's term changes, kept as stored (effectiveTerms enforces the limits when printing). */
const toTermsOverrides = (
  v: unknown,
): Record<string, ServiceTermsOverride> | undefined => {
  if (!isObject(v)) return undefined;
  const out: Record<string, ServiceTermsOverride> = {};
  for (const [key, raw] of Object.entries(v).slice(0, 40)) {
    if (!isObject(raw)) continue;
    const o: ServiceTermsOverride = {};
    if (typeof raw.revisions === "number") o.revisions = raw.revisions;
    for (const f of ["promise", "yourPart", "yoursWhenPaid", "afterDelivery", "readyIn"] as const) {
      const t = raw[f];
      if (typeof t === "string") o[f] = t.slice(0, 1000);
    }
    if (Array.isArray(raw.paymentPlans)) {
      o.paymentPlans = raw.paymentPlans.filter(isPlan);
    }
    if (Object.keys(o).length > 0) out[key.slice(0, 80)] = o;
  }
  return Object.keys(out).length > 0 ? out : undefined;
};

/** Fills every gap, so a half-written draft still renders. */
export const toContractContent = (v: unknown, today = ""): ContractContent => {
  const r = isObject(v) ? v : {};
  const template = isTemplate(r.template) ? r.template : "service";
  const base = emptyContractContent(template, today);
  const scopeOutline = toScopeOutline(r.scopeOutline);
  return {
    template,
    client: toParty(r.client),
    scopeItems: Array.isArray(r.scopeItems)
      ? r.scopeItems
          .filter((s): s is string => typeof s === "string")
          .slice(0, CONTRACT_LIMITS.scopeItems)
          .map((s) => s.slice(0, CONTRACT_LIMITS.scopeItemLength))
      : [],
    delivering: Array.isArray(r.delivering)
      ? r.delivering
          .filter((s): s is string => typeof s === "string" && s.trim() !== "")
          .slice(0, 40)
          .map((s) => s.slice(0, 80))
      : [],
    ...(toTermsOverrides(r.termsOverrides)
      ? { termsOverrides: toTermsOverrides(r.termsOverrides) }
      : {}),
    ...(scopeOutline ? { scopeOutline } : {}),
    scopeNotes: text(r.scopeNotes, CONTRACT_LIMITS.scopeNotesLength),
    commencementDate: isIsoDate(r.commencementDate)
      ? r.commencementDate
      : base.commencementDate,
    fee: Math.max(0, num(r.fee)),
    paymentPlan: isPlan(r.paymentPlan) ? r.paymentPlan : base.paymentPlan,
    stages: toStages(r.stages),
    packageId: isPackage(r.packageId) ? r.packageId : null,
    monthlyFee: Math.max(0, num(r.monthlyFee, base.monthlyFee)),
    minimumTermMonths: Math.max(
      1,
      Math.round(num(r.minimumTermMonths, base.minimumTermMonths)),
    ),
    retainerBilling: r.retainerBilling === "once_off" ? "once_off" : "monthly",
    monthlyDueDay: toMonthlyDueDay(r.monthlyDueDay),
    blueprintFee: Math.max(0, num(r.blueprintFee, base.blueprintFee)),
    blueprintCredit:
      typeof r.blueprintCredit === "boolean"
        ? r.blueprintCredit
        : base.blueprintCredit,
    sprintFee: Math.max(0, num(r.sprintFee, base.sprintFee)),
    sprintWeeks: Math.max(1, Math.round(num(r.sprintWeeks, base.sprintWeeks))),
    estimatedSprints: Math.max(
      1,
      Math.round(num(r.estimatedSprints, base.estimatedSprints)),
    ),
    equityPercent: Math.min(
      100,
      Math.max(0, num(r.equityPercent, base.equityPercent)),
    ),
    amendment: toAmendment(r.amendment),
  };
};

const toFile = (v: unknown): StoredContractFile | null =>
  isObject(v) && typeof v.path === "string" && typeof v.sha256 === "string"
    ? { path: v.path, sha256: v.sha256, bytes: num(v.bytes) }
    : null;

const toEvidence = (v: unknown): ContractSignatureEvidence | null =>
  isObject(v) && typeof v.signedAt === "number"
    ? {
        name: text(v.name, 300),
        title: text(v.title, 300),
        email: text(v.email, 300),
        emailVerifiedAt: millis(v.emailVerifiedAt),
        phone: text(v.phone, 50),
        signedAt: v.signedAt,
        ip: text(v.ip, 100),
        userAgent: text(v.userAgent, 500),
        imagePath: text(v.imagePath, 500),
        imageSha256: text(v.imageSha256, 64),
        contentSha256: text(v.contentSha256, 64),
      }
    : null;

/** The frozen document is server-written; trust its shape only loosely. */
const toDocument = (v: unknown): ContractDocument | null =>
  isObject(v) &&
  typeof v.title === "string" &&
  Array.isArray(v.sections) &&
  isObject(v.schedule)
    ? (v as unknown as ContractDocument)
    : null;

export const CONTRACT_IMPORT_SOURCES: readonly ContractImportSource[] = [
  "zoho",
  "google_docs",
  "other",
];

export const CONTRACT_IMPORT_SOURCE_LABELS: Record<
  ContractImportSource,
  string
> = {
  zoho: "Zoho Contracts",
  google_docs: "Google Docs e-signature",
  other: "Somewhere else",
};

const toImport = (v: unknown): ContractImport | null => {
  if (!isObject(v) || !isIsoDate(v.signedOn)) return null;
  const source = CONTRACT_IMPORT_SOURCES.find((s) => s === v.source);
  return {
    source: source ?? "other",
    signedOn: v.signedOn,
    clientSignerName: text(v.clientSignerName, 300),
    providerSignerName: text(v.providerSignerName, 300),
    importedAt: num(v.importedAt),
    importedBy: text(v.importedBy, 200),
  };
};

const count = (v: unknown): number | null =>
  typeof v === "number" && Number.isSafeInteger(v) && v >= 1 ? v : null;

export const toContract = (id: string, data: Raw): Contract => ({
  id,
  // An empty reference is a draft still waiting for a numbered client; only
  // a record with no reference field at all (never written by this admin)
  // falls back to the old style.
  reference:
    typeof data.reference === "string"
      ? text(data.reference, 60)
      : legacyContractReference(id),
  status: STATUSES.includes(data.status as ContractStatus)
    ? (data.status as ContractStatus)
    : "draft",
  clientId: text(data.clientId, 200) || null,
  clientNumber: count(data.clientNumber),
  contractNumber: count(data.contractNumber),
  leadId: text(data.leadId, 200) || null,
  parentContractId: text(data.parentContractId, 200) || null,
  quoteId: text(data.quoteId, 200) || null,
  jobId: text(data.jobId, 200) || null,
  content: toContractContent(data.content),
  notes: text(data.notes, CONTRACT_LIMITS.notesLength),
  createdAt: millis(data.createdAt),
  updatedAt: millis(data.updatedAt),
  sentAt: millis(data.sentAt),
  document: toDocument(data.document),
  contentSha256: text(data.contentSha256, 64) || null,
  unsignedPdf: toFile(data.unsignedPdf),
  clientSignature: toEvidence(data.clientSignature),
  clientSignedPdf: toFile(data.clientSignedPdf),
  countersignature: toEvidence(data.countersignature),
  signedPdf: toFile(data.signedPdf),
  signedAt: millis(data.signedAt),
  imported: toImport(data.imported),
  archivedAt: millis(data.archivedAt),
  archivedBy: text(data.archivedBy, 200) || null,
});

/**
 * What still stops the contract going out: its text (contractReadinessIssues)
 * and its reference. The server runs the same check before it sends.
 */
export const contractSendIssues = (
  contract: Pick<Contract, "reference" | "content">,
): string[] => [
  ...(contract.reference.trim()
    ? []
    : [
        "Give the contract its reference: link it to a client with a client number.",
      ]),
  ...contractReadinessIssues(contract.content),
];

// ─── Before sending ──────────────────────────────────────────────────────

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * What still has to be filled in before the contract can go out. Empty when
 * it is ready. The server runs the same check before it freezes and sends.
 */
export const contractReadinessIssues = (c: ContractContent): string[] => {
  const issues: string[] = [];
  if (!c.client.name.trim()) issues.push("Add the client's name.");
  if (c.client.email.trim() && !EMAIL.test(c.client.email.trim()))
    issues.push("Check the client's email: it does not look right.");
  if (!c.client.signatoryName.trim())
    issues.push("Add the name of the person signing for the client.");
  if (
    !c.scopeItems.some((s) => s.trim()) &&
    !(c.template === "venture" && c.scopeNotes.trim())
  )
    issues.push(
      c.template === "venture"
        ? "Describe the venture."
        : "Add at least one scope item.",
    );
  if (!isIsoDate(c.commencementDate)) issues.push("Set the commencement date.");

  switch (c.template) {
    case "service":
      if (c.fee <= 0) issues.push("Set the project fee.");
      if (c.paymentPlan === "phase_by_phase") {
        if (c.stages.length === 0)
          issues.push("Add the Pay As We Build stages.");
        const sum = c.stages.reduce((s, st) => s + st.amount, 0);
        if (c.stages.length > 0 && Math.abs(sum - c.fee) > 0.5)
          issues.push("The stages must add up to the project fee.");
      }
      break;
    case "retainer":
    case "venture":
      if (c.monthlyFee <= 0) issues.push("Set the monthly fee.");
      if (
        c.template === "venture" &&
        (c.equityPercent <= 0 || c.equityPercent > 100)
      )
        issues.push("Set the equity stake.");
      break;
    case "sprint":
      if (c.sprintFee <= 0) issues.push("Set the stage fee.");
      break;
  }
  return issues;
};

// ─── Settings ────────────────────────────────────────────────────────────

export const emptyBankDetails = (): ContractBankDetails => ({
  bankName: "",
  accountHolder: "",
  accountNumber: "",
  branchCode: "",
  accountType: "",
});

/**
 * Settings saved before 1 Oct 2026 may hold a free-text `referenceNote`.
 * It is not read: every payment now has its own reference, and the next
 * save drops the field (the rules refuse it).
 */
export const toContractSettings = (
  data: Raw | null | undefined,
): ContractSettings => {
  const r = data ?? {};
  const b = isObject(r.bank) ? r.bank : {};
  return {
    bank: {
      bankName: text(b.bankName, 120),
      accountHolder: text(b.accountHolder, 120),
      accountNumber: text(b.accountNumber, 40),
      branchCode: text(b.branchCode, 20),
      accountType: text(b.accountType, 60),
    },
    signatoryName: text(r.signatoryName, 120) || DEFAULT_CONTRACT_SIGNATORY,
    signatoryTitle: text(r.signatoryTitle, 120),
  };
};

/** True once there is enough to pay into: bank, holder and account number. */
export const hasBankDetails = (bank: ContractBankDetails): boolean =>
  Boolean(
    bank.bankName.trim() &&
    bank.accountHolder.trim() &&
    bank.accountNumber.trim(),
  );
